/**
 * Webtech HR Engine™ - Pricing Page
 * Page de sélection du plan avec tarification par tranches (annuel uniquement)
 */

import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle, CardDescription, CardFooter } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Slider } from '../components/ui/slider';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import api from '../lib/api';
import {
  Check,
  X,
  Sparkles,
  Users,
  Crown,
  ArrowRight,
  Loader2,
  Tag,
  Phone,
  Mail,
  Shield,
  Building2,
  CreditCard
} from 'lucide-react';

const FEATURES_FREE = [
  { name: '1 utilisateur', included: true },
  { name: '1 module : HR People Mapping™', included: true },
  { name: 'Trombinoscope complet', included: true },
  { name: 'Organigramme basique', included: true },
  { name: 'Sans carte bancaire', included: true },
];

const FEATURES_PRO = [
  { name: 'Tous les modules débloqués', included: true },
  { name: 'Analyse IA & recommandations', included: true },
  { name: 'Évaluations 180° / 360°', included: true },
  { name: 'Rapports PDF automatiques', included: true },
  { name: 'Support prioritaire', included: true },
];

const formatCurrency = (amount) => {
  if (!amount) return 'Sur devis';
  return new Intl.NumberFormat('fr-FR').format(amount) + ' F CFA';
};

export default function PricingPage() {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  
  const [loading, setLoading] = useState(true);
  const [subscription, setSubscription] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [freePlan, setFreePlan] = useState({ maxUsers: 3, maxEmployees: 10 });
  const [userCount, setUserCount] = useState(10);
  const [subscribing, setSubscribing] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoValidated, setPromoValidated] = useState(null);
  const [validatingPromo, setValidatingPromo] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [pricingRes, subRes] = await Promise.all([
        api.get('/billing/pricing'),
        api.get('/billing/subscription').catch(() => ({ data: null }))
      ]);
      
      if (pricingRes.data?.tiers) {
        setTiers(pricingRes.data.tiers);
      }
      if (pricingRes.data?.plans?.free) {
        setFreePlan(pricingRes.data.plans.free);
      }
      if (subRes.data?.subscription) {
        setSubscription(subRes.data.subscription);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Erreur lors du chargement des tarifs');
    } finally {
      setLoading(false);
    }
  };

  // Trouver la tranche correspondante au nombre d'utilisateurs
  const getCurrentTier = () => {
    for (const tier of tiers) {
      if (tier.contactRequired) continue;
      const minUsers = tier.minUsers || 0;
      const maxUsers = tier.maxUsers;
      if (maxUsers === null || maxUsers === undefined) {
        if (userCount >= minUsers) return tier;
      } else if (minUsers <= userCount && userCount <= maxUsers) {
        return tier;
      }
    }
    // Si plus de 1000 utilisateurs, retourner enterprise
    return tiers.find(t => t.contactRequired) || null;
  };

  const currentTier = getCurrentTier();
  const annualPrice = currentTier?.priceYearly || 0;
  const isEnterprise = currentTier?.contactRequired || userCount > 1000;

  // Calculer l'économie par rapport au prix par utilisateur (si on avait un prix unitaire)
  const economyPercent = 20; // Économie fixe de 20% sur l'annuel

  const validatePromoCode = async () => {
    if (!promoCode.trim()) return;
    
    setValidatingPromo(true);
    try {
      const response = await api.post('/billing/promo-code/validate', null, {
        params: { code: promoCode, user_count: userCount }
      });
      setPromoValidated(response.data);
      toast.success('Code promo appliqué !');
    } catch (error) {
      setPromoValidated(null);
      toast.error('Code promo invalide ou expiré');
    } finally {
      setValidatingPromo(false);
    }
  };

  const handleSubscribe = async () => {
    if (!isAdmin) {
      toast.error('Seul un administrateur peut modifier l\'abonnement');
      return;
    }
    
    if (isEnterprise) {
      window.location.href = `mailto:contact@hrengine.app?subject=Demande de devis Entreprise&body=Bonjour,%0A%0ANous sommes intéressés par le plan Entreprise pour ${userCount} utilisateurs.%0A%0AEntreprise: ${user?.companyName || ''}%0AContact: ${user?.email || ''}`;
      return;
    }
    
    setSubscribing(true);
    try {
      const response = await api.post('/billing/subscription/upgrade', {
        userCount: userCount,
        frequency: 'yearly',
        tierId: currentTier?.id,
        promoCode: promoValidated?.code || null
      });
      
      if (response.data.paymentUrl) {
        window.location.href = response.data.paymentUrl;
      } else {
        toast.success('Abonnement mis à jour !');
        navigate('/settings');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur lors de la souscription');
    } finally {
      setSubscribing(false);
    }
  };

  const isPro = subscription?.plan === 'pro';

  if (loading) {
    return (
      <DashboardLayout title="Tarification">
        <div className="max-w-5xl mx-auto space-y-8">
          <div className="text-center space-y-2">
            <Skeleton className="h-10 w-64 mx-auto" />
            <Skeleton className="h-6 w-96 mx-auto" />
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            <Skeleton className="h-[500px] w-full" />
            <Skeleton className="h-[500px] w-full" />
          </div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Tarification">
      <div className="max-w-5xl mx-auto space-y-8">
        {/* Header */}
        <div className="text-center">
          <h1 className="text-3xl font-bold text-slate-900">Choisissez votre plan</h1>
          <p className="text-slate-600 mt-2">Débloquez tout le potentiel de Webtech HR Engine™</p>
        </div>

        {/* Pricing cards - 2 columns */}
        <div className="grid md:grid-cols-2 gap-8">
          {/* FREE Plan */}
          <Card className={`relative ${!isPro ? 'ring-2 ring-slate-200' : ''}`}>
            <CardHeader className="pb-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center">
                  <Shield className="w-6 h-6 text-slate-600" />
                </div>
                <div>
                  <CardTitle className="text-xl">STARTER</CardTitle>
                  <CardDescription>Pour Startups & PME en croissance</CardDescription>
                </div>
              </div>
              
              <div className="mt-4">
                <span className="text-5xl font-bold text-slate-900">0</span>
                <span className="text-slate-500 text-lg ml-2">F CFA</span>
                <span className="text-slate-400 ml-1">de 20 à 100 employés</span>
              </div>
            </CardHeader>
            
            <CardContent className="pb-6">
              <ul className="space-y-4">
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-slate-700">{freePlan.maxUsers} utilisateur{freePlan.maxUsers > 1 ? 's' : ''}</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-slate-700">{freePlan.maxEmployees} employés maximum</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-slate-700">HR People Mapping™</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-slate-700">Trombinoscope complet</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-slate-700">Organigramme basique</span>
                </li>
                <li className="flex items-center gap-3">
                  <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                  <span className="text-slate-700">Sans carte bancaire</span>
                </li>
              </ul>
              
              <p className="text-sm text-slate-500 italic mt-6">
                Parfait pour découvrir et commencer.
              </p>
            </CardContent>
            
            <CardFooter>
              <Button 
                variant="outline" 
                className="w-full h-12 text-base"
                disabled
              >
                {isPro ? 'Votre ancien plan' : 'Commencer gratuitement'}
              </Button>
            </CardFooter>
          </Card>

          {/* PRO Plan */}
          <Card className={`relative border-2 border-webtech-blue ${isPro ? 'ring-2 ring-amber-400' : ''}`}>
            {/* Recommended badge */}
            <div className="absolute -top-3 right-6">
              <Badge className="bg-amber-500 text-white px-3 py-1">
                <Sparkles className="w-3 h-3 mr-1" />
                RECOMMANDÉ
              </Badge>
            </div>
            
            <CardHeader className="pb-6">
              <div className="flex items-center gap-3 mb-4">
                <div className="w-12 h-12 rounded-xl bg-webtech-blue flex items-center justify-center">
                  <Crown className="w-6 h-6 text-white" />
                </div>
                <div>
                  <CardTitle className="text-xl">BUSINESS</CardTitle>
                  <CardDescription>Pour entreprises, groupes et organisations · +100 employés</CardDescription>
                </div>
              </div>
              
              {/* Price display */}
              {isEnterprise ? (
                <div className="mt-4">
                  <span className="text-3xl font-bold text-slate-900">Sur devis</span>
                  <p className="text-slate-500 mt-1">Contactez-nous pour un tarif personnalisé</p>
                </div>
              ) : (
                <div className="mt-4">
                  <span className="text-5xl font-bold text-webtech-blue">
                    {formatCurrency(annualPrice).replace(' F CFA', '')}
                  </span>
                  <span className="text-slate-500 text-lg ml-2">F CFA / an</span>
                </div>
              )}
            </CardHeader>
            
            <CardContent className="pb-6 space-y-6">
              {/* User count slider */}
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-medium text-slate-700">Nombre d'utilisateurs</span>
                  <span className="text-lg font-bold text-webtech-blue">{userCount}</span>
                </div>
                
                <Slider
                  value={[userCount]}
                  onValueChange={(value) => setUserCount(value[0])}
                  min={10}
                  max={1000}
                  step={10}
                  className="w-full"
                />
                
                <div className="flex justify-between text-xs text-slate-400">
                  <span>Min. 10</span>
                  <span>1000+</span>
                </div>
              </div>

              {/* Tier info box */}
              {!isEnterprise && currentTier && (
                <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                  <div className="flex justify-between items-center mb-2">
                    <span className="text-sm text-slate-600">Total annuel</span>
                    <span className="text-lg font-bold text-slate-900">{formatCurrency(annualPrice)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-slate-600">Tranche</span>
                    <Badge className="bg-webtech-blue text-white">{currentTier.name}</Badge>
                  </div>
                  {currentTier.maxEmployees && (
                    <div className="flex justify-between items-center mt-2">
                      <span className="text-sm text-slate-600">Max employés</span>
                      <span className="text-sm font-medium">{currentTier.maxEmployees.toLocaleString()}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Enterprise info */}
              {isEnterprise && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
                  <div className="flex items-center gap-2 text-amber-700">
                    <Building2 className="w-5 h-5" />
                    <span className="font-medium">Offre Entreprise</span>
                  </div>
                  <p className="text-sm text-amber-600 mt-2">
                    Pour plus de 1000 utilisateurs, contactez-nous pour un devis personnalisé avec accompagnement dédié.
                  </p>
                </div>
              )}

              {/* Features */}
              <ul className="space-y-3">
                {FEATURES_PRO.map((feature, idx) => (
                  <li key={idx} className="flex items-center gap-3">
                    <Check className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-slate-700">{feature.name}</span>
                  </li>
                ))}
              </ul>
              
              <p className="text-xs text-slate-500 text-center">
                ○ Résiliable à tout moment. Satisfait ou remboursé 14 jours.
              </p>
            </CardContent>
            
            <CardFooter>
              {isEnterprise ? (
                <Button 
                  className="w-full h-12 text-base bg-slate-800 hover:bg-slate-700"
                  onClick={handleSubscribe}
                >
                  <Phone className="w-5 h-5 mr-2" />
                  Contactez-nous
                </Button>
              ) : (
                <Button 
                  className="w-full h-12 text-base bg-webtech-blue hover:bg-webtech-blue-hover"
                  onClick={handleSubscribe}
                  disabled={subscribing || isPro}
                >
                  {subscribing ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : isPro ? (
                    'Plan actuel'
                  ) : (
                    <>
                      Passer au PRO
                      <ArrowRight className="w-5 h-5 ml-2" />
                    </>
                  )}
                </Button>
              )}
            </CardFooter>
          </Card>
        </div>

        {/* Promo code section */}
        <Card className="max-w-md mx-auto">
          <CardContent className="py-4">
            <div className="flex items-center gap-2 mb-3">
              <Tag className="w-5 h-5 text-slate-500" />
              <span className="font-medium">Code promo</span>
            </div>
            <div className="flex gap-2">
              <Input
                placeholder="Entrez votre code"
                value={promoCode}
                onChange={(e) => setPromoCode(e.target.value.toUpperCase())}
                className="flex-1"
              />
              <Button 
                variant="outline"
                onClick={validatePromoCode}
                disabled={validatingPromo || !promoCode.trim()}
              >
                {validatingPromo ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Appliquer'}
              </Button>
            </div>
            {promoValidated && (
              <p className="text-sm text-green-600 mt-2">
                ✓ {promoValidated.description || 'Code promo appliqué'}
              </p>
            )}
          </CardContent>
        </Card>

        {/* Pricing tiers table */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Grille tarifaire annuelle</CardTitle>
            <CardDescription>Tarifs dégressifs selon le nombre d'utilisateurs</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b bg-slate-50">
                    <th className="text-left py-3 px-4 font-medium">Tranche</th>
                    <th className="text-left py-3 px-4 font-medium">Utilisateurs</th>
                    <th className="text-right py-3 px-4 font-medium">Prix annuel</th>
                    <th className="text-right py-3 px-4 font-medium">Max employés</th>
                  </tr>
                </thead>
                <tbody>
                  {tiers.filter(t => !t.contactRequired).map((tier) => (
                    <tr 
                      key={tier.id} 
                      className={`border-b ${currentTier?.id === tier.id ? 'bg-blue-50' : ''}`}
                    >
                      <td className="py-3 px-4 font-medium">
                        {tier.name}
                        {currentTier?.id === tier.id && (
                          <Badge className="ml-2 bg-webtech-blue text-white text-xs">Sélectionné</Badge>
                        )}
                      </td>
                      <td className="py-3 px-4">{tier.minUsers} - {tier.maxUsers || '∞'}</td>
                      <td className="py-3 px-4 text-right font-semibold">
                        {formatCurrency(tier.priceYearly)}
                      </td>
                      <td className="py-3 px-4 text-right">{tier.maxEmployees?.toLocaleString() || '∞'}</td>
                    </tr>
                  ))}
                  <tr className="bg-slate-50">
                    <td className="py-3 px-4 font-medium">Entreprise</td>
                    <td className="py-3 px-4">1000+</td>
                    <td className="py-3 px-4 text-right font-semibold text-amber-600">Sur devis</td>
                    <td className="py-3 px-4 text-right">Illimité</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>

        {/* Contact section */}
        <div className="text-center text-sm text-slate-500">
          <p>Des questions ? Contactez notre équipe commerciale</p>
          <div className="flex justify-center gap-4 mt-2">
            <a href="mailto:contact@hrengine.app" className="flex items-center gap-1 text-webtech-blue hover:underline">
              <Mail className="w-4 h-4" />
              contact@hrengine.app
            </a>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}
